import { Input, RadioGroup } from '@panwds/react-form';
import { useTranslate } from '../../../customHooks';
import { subscriptionEmailValidator } from '../validators';
import {
    SUBSCRIPTION_FIELDS,
    SUBSCRIPTION_I18N_KEYS,
    SUBSCRIPTION_METRICS,
} from '../constants';

/**
 * Shared form field components for subscription forms
 * These components provide consistent field configuration across all subscription components
 */

interface SubscriptionAccountIdFieldProps {
    value: string;
    dataMetrics?: string;
    dataTestId?: string;
}

/**
 * AWS Account ID field - always read-only, displays the account ID from URL params
 */
export const SubscriptionAccountIdField = ({
    value,
    dataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_FIRST_NAME,
    dataTestId
}: SubscriptionAccountIdFieldProps) => {
    const translate = useTranslate();

    return (
        <Input
            name={SUBSCRIPTION_FIELDS.ACCOUNT_ID}
            label={translate(SUBSCRIPTION_I18N_KEYS.AWS_ACCOUNT_ID)}
            dataMetrics={dataMetrics}
            dataTestId={dataTestId || dataMetrics}
            requiredAsterisk
            value={value}
            readOnly
            placeholder=""
        />
    );
};

interface SubscriptionEmailFieldProps {
    dataMetrics?: string;
    dataTestId?: string;
}

/**
 * Email field with pre-configured validation (required, not blocked domain, valid email format)
 */
export const SubscriptionEmailField = ({
    dataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_EMAIL,
    dataTestId
}: SubscriptionEmailFieldProps) => {
    const translate = useTranslate();

    return (
        <Input
            name={SUBSCRIPTION_FIELDS.USER_NAME}
            type="email"
            label={translate(SUBSCRIPTION_I18N_KEYS.EMAIL)}
            dataMetrics={dataMetrics}
            dataTestId={dataTestId || dataMetrics}
            requiredAsterisk
            placeholder=""
            register={{
                validate: subscriptionEmailValidator
            }}
        />
    );
};

interface SubscriptionBillingCodeFieldProps {
    dataMetrics?: string;
    dataTestId?: string;
    value?: string;
    readOnly?: boolean;
}

/**
 * Billing Association Code field
 */
export const SubscriptionBillingCodeField = ({
    dataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_BILLING_CODE,
    dataTestId,
    value,
    readOnly = false
}: SubscriptionBillingCodeFieldProps) => {
    const translate = useTranslate();

    return (
        <Input
            name={SUBSCRIPTION_FIELDS.BILLING_ASSOCIATION_CODE}
            label={translate(SUBSCRIPTION_I18N_KEYS.BILLING_CODE)}
            dataMetrics={dataMetrics}
            dataTestId={dataTestId || dataMetrics}
            requiredAsterisk
            value={value}
            readOnly={readOnly}
            placeholder=""
        />
    );
};

interface HasBillingCodeRadioGroupProps {
    dataMetrics?: string;
    dataTestId?: string;
}

/**
 * Radio group asking if user has a billing association code
 */
export const HasBillingCodeRadioGroup = ({
    dataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_HAS_BILLING_CODE,
    dataTestId
}: HasBillingCodeRadioGroupProps) => {
    const translate = useTranslate();

    return (
        <RadioGroup
            horizontal
            label={translate(SUBSCRIPTION_I18N_KEYS.HAVE_BILLING_CODE)}
            name={SUBSCRIPTION_FIELDS.HAS_BILLING_ASSOCIATION_CODE}
            dataMetrics={dataMetrics}
            dataTestId={dataTestId || dataMetrics}
            requiredAsterisk
            tooltip={translate(SUBSCRIPTION_I18N_KEYS.BILLING_CODE_TOOLTIP)}
            items={[{
                value: 'Yes',
                children: translate(SUBSCRIPTION_I18N_KEYS.YES),
            }, {
                value: 'No',
                children: translate(SUBSCRIPTION_I18N_KEYS.NO),
            }]}
        />
    );
};

interface SubscriptionNameFieldsProps {
    firstNameDataMetrics?: string;
    lastNameDataMetrics?: string;
    firstNameDataTestId?: string;
    lastNameDataTestId?: string;
}

/**
 * First and Last Name fields - shown for trial users without billing code
 */
export const SubscriptionNameFields = ({
    firstNameDataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_FIRST_NAME,
    lastNameDataMetrics = SUBSCRIPTION_METRICS.SIGN_UP_LAST_NAME,
    firstNameDataTestId,
    lastNameDataTestId
}: SubscriptionNameFieldsProps) => {
    const translate = useTranslate();

    return (
        <>
            <Input
                name={SUBSCRIPTION_FIELDS.FIRST_NAME}
                label={translate(SUBSCRIPTION_I18N_KEYS.NAME)}
                dataMetrics={firstNameDataMetrics}
                dataTestId={firstNameDataTestId || firstNameDataMetrics}
                requiredAsterisk
                placeholder=""
            />
            <Input
                name={SUBSCRIPTION_FIELDS.LAST_NAME}
                label={translate(SUBSCRIPTION_I18N_KEYS.LAST_NAME)}
                dataMetrics={lastNameDataMetrics}
                dataTestId={lastNameDataTestId || lastNameDataMetrics}
                requiredAsterisk
                placeholder=""
            />
        </>
    );
};
